import { Head, Link as InertiaLink } from "@inertiajs/react";
import {
  Badge,
  Box,
  Button,
  Callout,
  Flex,
  Grid,
  Heading,
  Link,
  Section,
  Separator,
  Text,
} from "@radix-ui/themes";
import {
  ArrowLeft,
  ArrowRight,
  Calendar,
  Info,
  MapPin,
  Trophy,
} from "lucide-react";
import { ManagerEventsShowProps } from "@/types/manager_events";
import ApplicationQueue from "@/shared/track_manager/ApplicationQueue";
import ApplicationsCards from "@/shared/track_manager/ApplicationsCards";
import AutoApprovalCard from "@/shared/manager/event/AutoApprovalCard";
import ApplicationWindowCard from "@/shared/manager/event/ApplicationWindowCard";
import WaitlistCard from "@/shared/manager/event/WaitlistCard";
import DayOfCard from "@/shared/manager/event/DayOfCard";
import useIsMobile from "@/shared/hooks/useIsMobile";

/**
 * One event's management page. The counterpart to the index's detail drawer:
 * everything that drawer shows is still there, but the applications are the
 * point of the page rather than a count.
 *
 * Rendered for both manager sections — every URL it uses arrives as a prop.
 */
export default function ManagerEventsShow({
  event,
  applications,
  paths,
}: ManagerEventsShowProps) {
  const isMobile = useIsMobile();

  return (
    <div className="flex">
      <Head title={event.name} />
      <div className="grow min-w-0">
        <Section size="1">
          {/* The queue pages below this one run their tables edge-to-edge on
              desktop; this page is headings and cards, so it keeps a gutter at
              every width. */}
          <Box px={{ initial: "3", sm: "4" }}>
            <Button asChild variant="ghost" size="1" mb="3">
              <InertiaLink href={paths.index}>
                <ArrowLeft size={14} />
                All events
              </InertiaLink>
            </Button>

            {/* Identity */}
            <Flex direction="column" gap="2" mb="4">
              <Flex align="center" gap="3" wrap="wrap">
                <Heading size="6">{event.name}</Heading>
                <Badge
                  variant="soft"
                  color={event.is_published ? "green" : "gray"}
                >
                  {event.is_published
                    ? "Published"
                    : event.publish_at === null
                      ? "Draft"
                      : `Publishes ${event.publish_at}`}
                </Badge>
              </Flex>
              <Flex align="center" justify="between" gap="4" wrap="wrap">
                {/* What this event is, on the left. */}
                <Flex align="center" gap="4" wrap="wrap">
                  <Flex align="center" gap="1">
                    <Calendar size={14} />
                    <Text size="2" color="gray">
                      {event.date}
                    </Text>
                  </Flex>
                  <Flex align="center" gap="1">
                    <MapPin size={14} />
                    <Text size="2" color="gray">
                      {event.track_name}
                    </Text>
                  </Flex>
                  {event.season_names.length > 0 && (
                    <Flex align="center" gap="1">
                      <Trophy size={14} />
                      <Text size="2" color="gray">
                        {event.season_names.join(", ")}
                      </Text>
                    </Flex>
                  )}
                </Flex>
                {/* Where to go from it, on the right. Check-in lives here as
                    well as in DayOfCard: the card only appears on the day, and
                    the gate list is worth reaching before then. */}
                <Flex align="center" gap="3">
                  <Link href={event.show_path} size="2">
                    <Flex align="center" gap="1">
                      View public page
                      <ArrowRight size={12} />
                    </Flex>
                  </Link>
                  <Button asChild variant="soft" size="2">
                    <InertiaLink href={event.participant_check_in_path}>
                      Participant Check In
                    </InertiaLink>
                  </Button>
                  <Button asChild variant="soft" color="gray" size="2">
                    <InertiaLink href={event.edit_path}>Edit Event</InertiaLink>
                  </Button>
                </Flex>
              </Flex>
            </Flex>

            {/* State of play. Each card decides for itself whether it has
                anything to say — an event with no waitlist and no auto-approval
                shows the window alone. */}
            <Grid columns={{ initial: "1", sm: "2", lg: "3" }} gap="3" mb="4">
              <ApplicationWindowCard
                status={event.applications_status}
                openAt={event.applications_open_at}
                closeAt={event.applications_close_at}
              />
              <AutoApprovalCard autoApproval={event.auto_approval} />
              <WaitlistCard applications={applications} />
              {event.day_of && (
                <DayOfCard
                  dayOf={event.day_of}
                  checkInPath={event.participant_check_in_path}
                />
              )}
            </Grid>

            {/* The applications this page exists for are not the ones stuck
                behind a credential, so say which is which. */}
            {event.blocked_on_credential_count > 0 && (
              <Callout.Root color="amber" mb="4">
                <Callout.Icon>
                  <Info size={16} />
                </Callout.Icon>
                <Callout.Text>
                  {event.blocked_on_credential_count}{" "}
                  {event.blocked_on_credential_count === 1
                    ? "application is"
                    : "applications are"}{" "}
                  waiting on an unreviewed annual credential. Approving the
                  credential decides the application.
                </Callout.Text>
              </Callout.Root>
            )}

            <Separator size="4" mb="4" />

            <Heading size="4" mb="3">
              Applications ({applications.length})
            </Heading>

            {isMobile ? (
              <ApplicationsCards applications={applications} />
            ) : (
              <ApplicationQueue
                applications={applications}
                scope="event"
                emptyMessage="Nobody has applied to this event yet."
              />
            )}
          </Box>
        </Section>
      </div>
    </div>
  );
}
